import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import { AdminApi, DropdownElement } from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import Icon from 'src/ui/atoms/icon';
import { debounce } from 'lodash';

interface AddGroupUserModalProps {
  onSaveUser: () => void;
  onClose: () => void;
  groupId: string;
}

interface AddUserFields {
  user: string;
}

export const Form = makeModalForm<AddUserFields>();

const AddGroupUserModal: FunctionComponent<AddGroupUserModalProps> = ({ onSaveUser, onClose, groupId }) => {
  const { makeAuthenticatedApi, withApi, error } = useApiCall();
  const [users, setUsers] = useState<DropdownElement[]>([]);

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const onSubmit = useCallback((fieldsValues: StateValues<AddUserFields>) => {
    withApi(async () => {
      await adminApi.adminGroupsUsersUpdate({ id: groupId, userId: fieldsValues.user });

      onSaveUser();
    });
  }, []);

  const isPhone = useIsMedia(DEVICES.phone);

  const fetchUsers = useCallback(
    (searchQuery: string = '', action?: string) => {
      withApi(async () => {
        if (action === 'menu-close' && searchQuery === '') {
          return;
        }
        const response = await adminApi.adminUsersDropdownList({
          search: searchQuery,
          isActive: true,
          groupsExclude: groupId,
          limit: 100,
        });
        setUsers(response.results || []);
      });
    },
    [withApi, adminApi],
  );

  const searchUserValue = useCallback(
    (value: string, action?: string) => {
      fetchUsers(value, action);
    },
    [fetchUsers],
  );

  const debouncedUserSearch = useMemo(() => debounce(searchUserValue, 500), [searchUserValue]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addGroupUserModal"
        onSubmit={(values: StateValues<AddUserFields>) => {
          onSubmit(values);
        }}
        error={error}
      >
        <ModalHeader>
          <Translate name={'addGroupUserModal.title'} />
        </ModalHeader>
        <ModalContent>
          <>
            <Form.Group
              name="user"
              required={true}
              tooltip={
                <Tooltip
                  content={<Translate name="addGroupUserModal.fields.user.tooltip" />}
                  position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                  maxWidth={200}
                >
                  <Icon name="question" circular={true} size="large" color="info" padded="none" />
                </Tooltip>
              }
            >
              <Form.Select
                options={users.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
                onInputChange={debouncedUserSearch}
              />
            </Form.Group>
          </>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddGroupUserModal;
